import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import Api from '../../../../../../core/services/api/api';
import BroadcastService from '../../../../../../core/services/broadcast';
import useIsMounted from '../../../../../hooks/use-is-mounted';
import useAccessLevels from '../../../../../hooks/use-access-levels';

/**
 * @typedef {object} ContactUsUnreadCountContextValue
 * @property {number} unreadCount
 * @property {() => Promise<void>} refreshUnreadCount
 * @property {boolean} loading
 */

/** @type {ContactUsUnreadCountContextValue} */
const fallbackValue = {
    unreadCount: 0,
    refreshUnreadCount: async () => undefined,
    loading: false,
};

const ContactUsUnreadCountContext = createContext(fallbackValue);

/**
 * Owns Contact Us unread-count state for the operator sidebar.
 * Fetches once on mount (when the feature is accessible) and re-fetches on broadcast
 * after list mutations — badges only read this value.
 *
 * @param {{children: React.ReactNode}} props
 */
export const ContactUsUnreadCountProvider = ({children}) => {
    const isMounted = useIsMounted();
    const operatorAccessLevels = useAccessLevels();
    const canAccess = operatorAccessLevels.packages.support.contactUsMessages;
    const [unreadCount, setUnreadCount] = useState(0);
    const [loading, setLoading] = useState(false);
    /** @type {React.MutableRefObject<Promise<void>|null>} */
    const inFlightRef = useRef(null);

    /**
     * Loads UnreadCount once; concurrent callers share the same request.
     */
    const refreshUnreadCount = useCallback(async () => {
        if (!operatorAccessLevels.packages.support.contactUsMessages) {
            setUnreadCount(0);
            return;
        }
        if (inFlightRef.current) {
            return inFlightRef.current;
        }
        inFlightRef.current = (async () => {
            if (!canAccess) return;
            setLoading(true);
            try {
                const response = await Api.getContactUsUnreadCount();
                if (!isMounted()) {
                    return;
                }
                setUnreadCount(response?.resultFlag ? (response?.data?.count ?? 0) : 0);
            } finally {
                if (isMounted()) {
                    setLoading(false);
                }
                inFlightRef.current = null;
            }
        })();
        return inFlightRef.current;
    }, [canAccess, isMounted, operatorAccessLevels.packages.support.contactUsMessages]);

    /**
     * Initial fetch when the feature package is available.
     */
    useEffect(() => {
        if (!canAccess) {
            setUnreadCount(0);
            return;
        }
        void refreshUnreadCount();
    }, [canAccess, refreshUnreadCount]);

    /**
     * Refresh when the list page mutates messages (mark/remove).
     */
    useEffect(() => {
        if (!canAccess) {
            return undefined;
        }
        const {close} = BroadcastService.addEventListener(
            BroadcastService.channelNames.contactUsMessagesUnreadCount,
            'message',
            () => {
                void refreshUnreadCount();
            },
        );
        return () => {
            close?.();
        };
    }, [canAccess, refreshUnreadCount]);

    const value = useMemo(() => ({
        unreadCount,
        refreshUnreadCount,
        loading,
    }), [loading, refreshUnreadCount, unreadCount]);

    return (
        <ContactUsUnreadCountContext.Provider value={value}>
            {children}
        </ContactUsUnreadCountContext.Provider>
    );
};

/**
 * Reads Contact Us unread count from the nearest provider.
 * Safe fallback when used outside the provider (count 0, no-op refresh).
 * @returns {ContactUsUnreadCountContextValue}
 */
export const useContactUsUnreadCount = () => (
    useContext(ContactUsUnreadCountContext) ?? fallbackValue
);

export default ContactUsUnreadCountProvider;
